import React, { useEffect, useState } from 'react';

import { useSiteSettings } from '../contexts/SiteSettingsContext';
import '../styles/components/site-logo.scss';

interface SiteLogoProps {
    className?: string;
    alt?: string;
    preferMobile?: boolean;
    /** Header/top-menu gibi yerlerde görsel yüklemeyi tamamen kapatır. */
    textOnly?: boolean;
    normalizeWinColor?: boolean;
}

function normalizeUrl(value?: string): string {
    const normalized = String(value || '').trim();
    if (!normalized) return '';

    if (/^(?:https?:|data:|blob:|\/\/)/i.test(normalized)) {
        return normalized;
    }

    if (normalized.startsWith('/')) {
        return normalized;
    }

    return `/${normalized.replace(/^\.\//, '')}`;
}

const SiteLogo: React.FC<SiteLogoProps> = ({
    className = '',
    alt,
    preferMobile = false,
    textOnly = false,
    normalizeWinColor = false,
}) => {
    const { settings } = useSiteSettings();
    const desktopLogo = normalizeUrl(settings.logo_url);
    const mobileLogo = normalizeUrl(settings.mobile_logo_url);
    const preferredSrc = preferMobile && mobileLogo ? mobileLogo : desktopLogo || mobileLogo;
    const siteName = String(settings.site_name || '').trim();

    const [hasLoadError, setHasLoadError] = useState(false);

    useEffect(() => {
        setHasLoadError(false);
    }, [preferredSrc, textOnly]);

    const showImage = !textOnly && Boolean(preferredSrc) && !hasLoadError;
    const showTextFallback = !showImage && Boolean(siteName);
    const resolvedAlt = String(alt ?? siteName).trim();
    const showWinColor = normalizeWinColor && showImage && /^zu[\s.]*win$/i.test(siteName);

    const rootClassName = [
        'site-logo',
        className,
        showTextFallback ? 'is-text' : '',
        textOnly ? 'is-text-only' : '',
        showWinColor ? 'has-win-color' : '',
    ]
        .filter(Boolean)
        .join(' ');

    return (
        <div className={rootClassName} aria-label={resolvedAlt || undefined}>
            {showImage ? (
                // `img` needs `onError` to fall back to text logo when CDN asset fails.
                // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
                <img
                    className="site-logo__img"
                    src={preferredSrc}
                    alt={resolvedAlt}
                    draggable={false}
                    loading="eager"
                    onError={() => {
                        setHasLoadError(true);
                    }}
                />
            ) : showTextFallback ? (
                <span className="site-logo__text">{siteName}</span>
            ) : null}
            {showWinColor && (
                <span
                    className="site-logo__win-color"
                    aria-hidden="true"
                    style={{ maskImage: `url(${JSON.stringify(preferredSrc)})` }}
                />
            )}
        </div>
    );
};

export default SiteLogo;
